import { useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUserNicks } from '@/hooks/mensag/useNIP17';
import { EditProfileForm } from '@/components/EditProfileForm';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, User, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ProfilePage() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { data: userNicks } = useUserNicks();

  const [isEditingNick, setIsEditingNick] = useState(false);
  const [newNickname, setNewNickname] = useState(user?.profile?.name || '');
  const [isChangingNick, setIsChangingNick] = useState(false);
  const [changeError, setChangeError] = useState('');

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Debes iniciar sesión para ver tu perfil
        </p>
        <Button onClick={() => navigate('/')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  const handleUpdateNickname = async () => {
    if (!newNickname.trim() || !user?.signer) return;

    try {
      setChangeError('');
      setIsChangingNick(true);

      // Update profile with new nickname
      const updatedProfile = {
        name: newNickname.trim(),
        display_name: newNickname.trim(),
        about: user.profile?.about || 'MensaG user',
        lud16: user.profile?.lud16,
        website: user.profile?.website,
        nip05: user.profile?.nip05,
        picture: user.profile?.picture
      };

      await publishEvent({ kind: 0, content: JSON.stringify(updatedProfile) });

      setIsEditingNick(false);
    } catch (error) {
      console.error('Error updating nickname:', error);
      setChangeError('Error al actualizar el nickname. Inténtalo de nuevo.');
    } finally {
      setIsChangingNick(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Mi Perfil
            </h2>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Profile Overview */}
          <Card>
            <CardHeader>
              <CardTitle>Información del Perfil</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user.profile?.picture} />
                  <AvatarFallback className="text-lg">
                    {user.profile?.name?.charAt(0) || user.pubkey.slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user.profile?.name || user.profile?.display_name || 'Usuario'}
                    </h3>
                    <Badge variant="secondary">MensaG</Badge>
                    {userNicks?.verified && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user.profile?.about || 'Usuario de MensaG'}
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge variant="outline">Conectado</Badge>
                    {userNicks?.nickname && (
                      <Badge variant="outline">
                        Nick: {userNicks.nickname}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Nickname Management */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nickname único
                </Label>
                <div className="flex items-center space-x-3 mt-2">
                  <Badge variant="secondary" className="px-3 py-1">
                    {user.profile?.name || 'Sin nickname'}
                  </Badge>
                  <Dialog open={isEditingNick} onOpenChange={setIsEditingNick}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <User className="h-4 w-4 mr-2" />
                        Cambiar nick
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Cambiar nickname</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="nickname">Nuevo nickname</Label>
                          <Input
                            id="nickname"
                            placeholder="Ej: usuario123"
                            value={newNickname}
                            onChange={(e) => setNewNickname(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !isChangingNick && handleUpdateNickname()}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Este será tu identificador único en MensaG
                          </p>
                        </div>
                        {changeError && (
                          <div className="flex items-center space-x-2 text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm">{changeError}</span>
                          </div>
                        )}
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            onClick={() => setIsEditingNick(false)}
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleUpdateNickname}
                            disabled={!newNickname.trim() || isChangingNick}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            {isChangingNick ? 'Actualizando...' : 'Actualizar'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Los otros usuarios te encontrarán usando este nickname.
                  {userNicks?.verified ? ' ✓ Verificado' : ' ⚠ Sin verificar'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                  Clave pública (hex)
                </label>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded break-all">
                  {user.pubkey}
                </code>
              </div>
            </CardContent>
          </Card>

          {/* Edit Profile Form */}
          <Card>
            <CardHeader>
              <CardTitle>Editar Perfil</CardTitle>
            </CardHeader>
            <CardContent>
              <EditProfileForm />
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Estadísticas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">0</div>
                  <div className="text-sm text-gray-500">Chats</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">0</div>
                  <div className="text-sm text-gray-500">Mensajes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">0</div>
                  <div className="text-sm text-gray-500">Contactos</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}